<template>
  <nav class="flex items-center bg-growth-25 px-6 py-3 text-black">
    <!-- Left section with logo -->
    <div class="flex items-center shrink-0">
      <!-- Logo (always visible) -->
      <NavLink class="flex items-center logo-container" to="/">
        <img src="@/assets/kairon_logo_orange.svg" alt="logo" class="w-7 h-7 rounded-sm logo-image" />
      </NavLink>

      <!-- Tenant selector -->
      <div v-if="isAuthenticated && (currentLayout === 'default' || currentLayout === 'portal')" class="relative ml-5">
        <button
          type="button"
          data-dropdown-trigger
          class="flex items-center space-x-2"
          :class="hasMutipleTenants ? 'cursor-pointer' : 'cursor-default'"
          :disabled="!hasMutipleTenants"
          @click="toggleTenantDropdown"
        >
          <span class="max-md:hidden text-base font-medium text-neutral-1000 sensitive" data-spoof-kind="tenant">{{ current_tenant_name }}</span>
          <font-awesome-icon v-if="hasMutipleTenants" :icon="['fas', 'chevron-down']" class="w-3 h-3 text-neutral-900" />
        </button>
        <div v-if="hasMutipleTenants" ref="dropdownContainer" class="absolute left-0 z-50">
          <Dropdown_Selector_Single
            v-if="tenantDropdownOpen"
            ref="tenant_selector"
            :options="availableTenants"
            :sensitive-labels="true"
            spoof-kind="tenant"
            @close="tenantDropdownOpen = false"
            @select="onTenantChange"
          />
        </div>
      </div>

      <!-- Entity selector (portal layout, worklist surfaces only — the Console
           resolves the coder's queue server-side and ignores this entirely). -->
      <div v-if="isAuthenticated && currentLayout === 'portal' && !isConsoleView" class="relative ml-5">
        <button
          type="button"
          class="flex items-center space-x-2 cursor-pointer"
          @click="toggleEntityDropdown"
        >
          <span class="text-base font-medium text-gray-900 sensitive">{{ isUnifiedWorklist ? 'All Entities' : current_entity_name }}</span>
          <font-awesome-icon :icon="['fas', 'chevron-down']" class="w-3 h-3 text-neutral-900" />
        </button>
        <div ref="dropdownContainer" class="absolute left-0 z-50">
          <Dropdown_Selector_Single
            v-if="entityDropdownOpen"
            ref="entity_selector"
            :options="entityOptions"
            :sensitive-labels="true"
            @close="toggleEntityDropdown"
            @select="onEntityChange"
          />
        </div>
      </div>

    </div>

    <!-- Center section with search -->
    <div v-if="isAuthenticated && currentLayout === 'default'" class="flex-1 flex justify-center px-8">
      <div class="w-full max-w-2xl">
        <Entity_Search ref="entitySearch" />
      </div>
    </div>
    <div v-else class="flex-1"></div>

    <!-- Right section with entity selector and profile -->
    <div v-if="isAuthenticated" class="flex items-center space-x-4">

      <!-- Phone status indicator -->
      <PhoneStatusIndicator />

      <!-- Role Preview dropdown -->
      <RolePreviewDropdown ref="rolePreviewDropdown" />

      <button
        v-if="hasAgent"
        type="button"
        class="group relative flex h-9 w-9 items-center justify-center rounded-md text-neutral-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800"
        :class="agentOverlay.open ? 'bg-white shadow-sm' : 'hover:bg-white'"
        :aria-label="agentOverlay.open ? 'Close the agent' : 'Open the agent'"
        :aria-pressed="agentOverlay.open"
        @click="agentOverlay.open = !agentOverlay.open"
      >
        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 3l1.8 4.6L18.5 9l-4.7 1.4L12 15l-1.8-4.6L5.5 9l4.7-1.4z" />
          <path d="M19 15l.8 2.2 2.2.8-2.2.8L19 21l-.8-2.2-2.2-.8 2.2-.8z" />
        </svg>
        <span class="pointer-events-none absolute right-0 top-full z-50 mt-2 w-max rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white opacity-0 transition-opacity duration-100 group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden="true">
          {{ agentOverlay.open ? 'Close the agent' : 'Open the agent' }}
        </span>
      </button>

      <!-- The app owns the launcher; Pylon still renders the chat overlay. -->
      <button
        v-if="hasPylonChat"
        type="button"
        class="group relative z-[2147483647] flex h-9 w-9 items-center justify-center rounded-md text-neutral-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800"
        :class="pylonChatOpen ? 'bg-white shadow-sm' : 'hover:bg-white'"
        :aria-label="pylonChatOpen ? 'Close support chat' : 'Open support chat'"
        :aria-pressed="pylonChatOpen"
        @pointermove="enableSupportChatTooltip"
        @click="toggleSupportChat"
      >
        <svg
          class="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M21 11.5a8.4 8.4 0 0 1-9 8.5 9.7 9.7 0 0 1-4.6-1.2L3 20l1.5-4a8.5 8.5 0 1 1 16.5-4.5Z" />
        </svg>
        <span
          v-if="pylonUnreadMessageCount > 0"
          class="absolute right-1.5 top-1.5 h-2 w-2 rounded-full border-2 border-white bg-primary-500"
          aria-hidden="true"
        />
        <span
          v-if="!pylonTooltipSuppressed"
          class="pointer-events-none absolute right-0 top-full z-50 mt-2 w-max rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white opacity-0 transition-opacity duration-100 group-hover:opacity-100 group-focus-visible:opacity-100"
          aria-hidden="true"
        >
          {{ pylonChatOpen ? 'Close support chat' : 'Open support chat' }}
        </span>
      </button>

      <!-- Profile section with dropdown -->
      <div class="relative" data-profile-dropdown>
        <button
          type="button"
          class="flex items-center space-x-2 cursor-pointer hover:bg-white px-2 py-1 rounded-md"
          @click="toggleProfileDropdown"
        >
          <Profile_Badge />
        </button>

        <!-- Profile dropdown menu -->
        <div 
          v-if="profileDropdownOpen" 
          class="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50"
          @click.stop
        >
          <div class="py-1">
            <ConditionalRouterLink
              to="/current_user"
              @click="profileDropdownOpen = false"
              class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Profile
            </ConditionalRouterLink>
            <a
              v-if="hasProviderMessaging"
              href="#"
              @click.prevent="goToProviderMessaging"
              class="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <span :class="{ 'font-semibold': unreadMessageCount > 0 }">Messages</span>
              <span
                v-if="unreadMessageCount > 0"
                class="ml-2 text-xs font-semibold text-primary-500"
              >
                ({{ unreadMessageCount }})
              </span>
            </a>
            <a
              href="#"
              @click.prevent="openReleasesPanel"
              class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Releases
            </a>
            <a
              href="https://kaironhealth.notion.site/Welcome-to-the-Kairon-Health-Wiki-1535fe5d7a9e806da2c9f05b9a7e920d"
              target="_blank"
              rel="noopener noreferrer"
              @click="profileDropdownOpen = false"
              class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Wiki
            </a>
            <div v-if="current_user?.is_superuser" class="bg-primary-50 border-t border-gray-200">
              <div class="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Superuser
              </div>
              <button
                type="button"
                role="switch"
                :aria-checked="$root.isDemoMode ? 'true' : 'false'"
                @click="toggleDemoMode"
                class="w-full text-left px-4 py-2 text-sm hover:bg-primary-100 flex items-center justify-between"
                :class="$root.isDemoMode ? 'text-amber-600 font-medium' : 'text-gray-700'"
              >
                Demo Mode
                <span
                  class="inline-block w-8 h-4 rounded-full transition-colors relative"
                  :class="$root.isDemoMode ? 'bg-amber-500' : 'bg-gray-300'"
                >
                  <span
                    class="absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform"
                    :class="$root.isDemoMode ? 'translate-x-4' : ''"
                  />
                </span>
              </button>
              <ConditionalRouterLink
                to="/superuser/dev-tools"
                @click="profileDropdownOpen = false"
                class="block px-4 py-2 text-sm !text-gray-700 hover:bg-primary-100"
              >
                Dev Tools
              </ConditionalRouterLink>
            </div>
            <a
              href="#"
              @click.prevent="handleLogout"
              class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-200"
            >
              Logout
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Release Notes Panel -->
    <ReleaseNotesPanel
      :is-open="releasesPanelOpen"
      @close="releasesPanelOpen = false"
    />
  </nav>
</template>

<script>
import Profile_Badge from '@/components/widgetcomponents/Profile_Badge.vue'
import { mapActions, mapGetters } from 'vuex'
import Entity_Search from '@/components/widgetcomponents/Search_Widgets/Entity_Search.vue'
import Dropdown_Selector_Single from '@/components/widgetcomponents/Dropdown_Selector/Dropdown_Selector_Single.vue'
import NavLink from './NavLink.vue'
import ConditionalRouterLink from './ConditionalRouterLink.vue'
import ReleaseNotesPanel from '@/components/ReleaseNotes/ReleaseNotesPanel.vue'
import RolePreviewDropdown from '@/components/navigation/RolePreviewDropdown.vue'
import PhoneStatusIndicator from '@/components/navigation/PhoneStatusIndicator.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import '@/assets/cm-tokens.css'
import { canAccessFeature } from '@/utils/access'
import { canUseAgentPopup } from '@/utils/agentAccess'
import { toggleDemoMode } from '@/utils/demoController'
import { agentOverlay } from '@/services/agentSessionController'
import { FEATURES } from '@/constants/features'
import {
  subscribeToPylonChatState,
  togglePylonWidget,
} from '@/services/pylonWidget'

export default {
  name: 'TopNavBar',
  components: {
    Dropdown_Selector_Single,
    Entity_Search,
    Profile_Badge,
    NavLink,
    ConditionalRouterLink,
    ReleaseNotesPanel,
    RolePreviewDropdown,
    PhoneStatusIndicator,
    FontAwesomeIcon,
  },
  data() {
    return {
      agentOverlay,
      tenantDropdownOpen: false,
      entityDropdownOpen: false,
      profileDropdownOpen: false,
      releasesPanelOpen: false,
      pylonChatOpen: false,
      pylonUnreadMessageCount: 0,
      pylonTooltipSuppressed: false,
      unsubscribePylonChatState: null,
    }
  },
  computed: {
    hasAgent() {
      return this.isAuthenticated && this.$route.name !== 'Agent' && canUseAgentPopup(this.current_user)
    },
    ...mapGetters('auth', [
      'isAuthenticated',
      'current_user',
      'unreadMessageCount',
      'hasActiveProviderMailbox',
    ]),
    ...mapGetters('tenant', [
      'availableTenants',
      'current_tenant_name',
      'current_tenant_slug',
      'isSecurityUser',
      'isTenantAdmin',
      'isRestrictedToPortal',
    ]),
    hasProviderMessaging() {
      return this.hasActiveProviderMailbox
        && canAccessFeature(FEATURES.MODULE_PROVIDER_MESSAGING, { superuserOverride: false })
    },
    hasPylonChat() {
      // The PostHog rollout swaps Pylon's stock bubble for this launcher; the
      // underlying tenant entitlement still controls whether Pylon boots.
      return this.$root.pylonBooted
        && this.$root.integratedPylonWidgetEnabled
        && !this.$root.isDemoMode
    },
    ...mapGetters('entity', [
      'availableEntities',
      'current_entity_name',
    ]),
    hasMutipleTenants() {
      return this.availableTenants && this.availableTenants.length > 1
    },
    currentLayout() {
      return this.$route.meta.layout
    },
    // Match on route NAME, not path: the unified worklist moved off bare
    // '/portal' onto '/portal/worklist' when the Console became the portal
    // landing page, and a path check silently falls through to the stale
    // localStorage entity name instead.
    isUnifiedWorklist() {
      return this.$route.name === 'PortalWorklist'
    },
    // Bare '/portal' renders the Console too, so both names count.
    isConsoleView() {
      return this.$route.name === 'PortalConsole' || this.$route.name === 'PortalOverview'
    },
    entityOptions() {
      // Selecting a practice is otherwise a one-way door — nothing in the
      // dropdown routes back to the entity-less unified worklist. String `id`
      // keeps the v-for key defined without colliding with the numeric
      // AppUserEntity ids the serializer returns.
      return [{ id: 'all', entity_id: null, name: 'All Entities' }, ...this.availableEntities]
    },
  },
  watch: {
    hasActiveProviderMailbox(newVal) {
      if (newVal) {
        this.startUnreadPolling()
      } else {
        this.stopUnreadPolling()
        this.$store.commit('auth/SET_UNREAD_MESSAGE_COUNT', 0)
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
    document.addEventListener('click', this.handleClickOutside)
    window.addEventListener('storage', this.handleStorageEvent)
    if (this.hasActiveProviderMailbox) {
      this.startUnreadPolling()
    }
    this.unsubscribePylonChatState = subscribeToPylonChatState((state) => {
      const chatVisibilityChanged = this.pylonChatOpen !== state.isOpen
      this.pylonChatOpen = state.isOpen
      this.pylonUnreadMessageCount = state.unreadMessageCount
      if (chatVisibilityChanged) this.pylonTooltipSuppressed = true
    })
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('storage', this.handleStorageEvent)
    this.stopUnreadPolling()
    this.unsubscribePylonChatState?.()
  },
  methods: {
    ...mapActions('tenant', ['setTenant_slug']),
    ...mapActions('entity', ['setEntity_id']),
    ...mapActions('auth', ['logout', 'fetchUnreadMessageCount']),
    toggleTenantDropdown() {
      if (this.tenantDropdownOpen) {
        this.tenantDropdownOpen = false
      } else {
        this.tenantDropdownOpen = true
        this.$nextTick(() => {
          this.adjustDropdownPosition('tenant_selector')
        })
      }
    },
    toggleEntityDropdown() {
      this.entityDropdownOpen = !this.entityDropdownOpen
      if (this.entityDropdownOpen) {
        this.adjustDropdownPosition('entity_selector')
      }
    },
    toggleProfileDropdown() {
      this.profileDropdownOpen = !this.profileDropdownOpen
    },
    enableSupportChatTooltip() {
      this.pylonTooltipSuppressed = false
    },
    toggleDemoMode() {
      toggleDemoMode()
    },
    toggleSupportChat() {
      if (!this.$root.isDemoMode) {
        this.pylonTooltipSuppressed = true
        togglePylonWidget()
      }
    },
    openReleasesPanel() {
      this.profileDropdownOpen = false
      this.releasesPanelOpen = true
    },
    goToProviderMessaging() {
      this.profileDropdownOpen = false
      this.$router.push({ name: 'CareManagement-ProviderMessaging' })
    },
    adjustDropdownPosition(refName) {
      this.$nextTick(() => {
        const dropdownContainer = this.$refs[refName].$refs.dropdownContainer
        if (dropdownContainer) {
          const dropdownElement = dropdownContainer.querySelector('.absolute')
          if (dropdownElement) {
            const dropdownRect = dropdownElement.getBoundingClientRect()
            let style = {}
            if (dropdownRect.right > window.innerWidth) {
              style.right = '0px'
            } else if (dropdownRect.left < 0) {
              style.left = '0px'
            }
            this.$refs[refName].dropdownStyle = style
          }
        }
      })
    },
    onTenantChange(tenant) {
      if (!tenant.slug || tenant.slug === this.current_tenant_slug) {
        return
      }
      this.setTenant_slug(tenant.slug)
      this.$router.go(0)
    },
    onEntityChange(entity) {
      // "All Entities" is a scope, not an entity — go to the unified worklist
      // and leave current_entity_id alone (clearing it just makes the store
      // re-default to the alphabetically-first practice on the next fetch).
      if (entity.entity_id === null) {
        if (this.$route.name !== 'PortalWorklist') {
          this.$router.push({ name: 'PortalWorklist' })
        }
        return
      }

      this.setEntity_id(entity.entity_id)

      if (this.$route.name === 'PortalEntity') {
        // Force a complete page reload
        window.location.href = `/portal/${entity.entity_id}`;
      } else {
        this.$router.push({
          name: 'PortalEntity',
          params: {
            entityid: entity.entity_id,
          }
        })
      }
    },
    handleKeydown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        this.$refs.entitySearch?.focusSearch()
      }
    },
    handleClickOutside(e) {
      // Close profile dropdown if clicking outside
      if (this.profileDropdownOpen) {
        const profileSection = e.target.closest('[data-profile-dropdown]')
        if (!profileSection) {
          this.profileDropdownOpen = false
        }
      }
      // Close role preview dropdown if clicking outside
      if (this.$refs.rolePreviewDropdown?.dropdownOpen) {
        const previewSection = e.target.closest('[data-role-preview-dropdown]')
        if (!previewSection) {
          this.$refs.rolePreviewDropdown.closeDropdown()
        }
      }
    },
    startUnreadPolling() {
      this.stopUnreadPolling()
      this.fetchUnreadMessageCount()
      this._unreadInterval = setInterval(() => this.fetchUnreadMessageCount(), 300000)
    },
    stopUnreadPolling() {
      if (this._unreadInterval) {
        clearInterval(this._unreadInterval)
        this._unreadInterval = null
      }
    },
    handleStorageEvent(e) {
      if (e.key !== 'unreadMessageCache' || !e.newValue) return
      try {
        const { count } = JSON.parse(e.newValue)
        if (typeof count === 'number') {
          this.$store.commit('auth/SET_UNREAD_MESSAGE_COUNT', count)
        }
      } catch {
        // ignore
      }
    },
    handleLogout() {
      this.profileDropdownOpen = false
      this.logout().then(() => {
        this.$router.push({ name: 'Login' })
      }).catch(error => {
        console.error('Logout failed:', error)
        // Still redirect to login even if logout fails
        this.$router.push({ name: 'Login' })
      })
    },
  },
}
</script>

<style scoped>
.router-link-active {
  color: #F97140;
}

/* Logo rotation animation */
.logo-container {
  transition: transform 0.3s ease;
}

.logo-image {
  transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}

.logo-container:hover .logo-image {
  transform: rotate(360deg);
}
</style>
